import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useActualizarUmbral, useUmbrales } from '@/hooks/useUmbrales';
import { cn } from '@/lib/utils';
import { COMPARADORES_UMBRAL, type ComparadorUmbral, type UmbralAlerta } from '@adelante/shared';
import { Check, Pencil, X } from 'lucide-react';
import { useMemo, useState } from 'react';

/**
 * `/admin/umbrales` — gestión de umbrales de alerta parametrizables.
 *
 * Quien edita: Admin + Ingeniería (ROLES_CONFIG en el backend). En el
 * frontend gateamos el link del header con `puedeConfigurar()`.
 *
 * Convención de claves:
 *   batches.*  → resaltado de celdas en "Datos de planta".
 *   lab.*      → umbrales de cumplimiento de resistencia.
 */

const ETIQUETA_COMPARADOR: Record<ComparadorUmbral, string> = {
  gte_abs: '|v| ≥ umbral',
  gt_abs: '|v| > umbral',
  gte: 'v ≥ umbral',
  gt: 'v > umbral',
  lte: 'v ≤ umbral',
  lt: 'v < umbral',
};

export function AdminUmbralesPantalla() {
  const consulta = useUmbrales();
  const actualizar = useActualizarUmbral();

  const umbrales = consulta.data?.data?.umbrales ?? [];
  const errorBackend = consulta.data?.error?.mensaje ?? null;

  // Agrupar por grupo (prefijo antes del primer punto).
  const grupos = useMemo(() => {
    const m = new Map<string, UmbralAlerta[]>();
    for (const u of umbrales) {
      const g = u.clave.split('.')[0] ?? 'otros';
      const arr = m.get(g) ?? [];
      arr.push(u);
      m.set(g, arr);
    }
    return Array.from(m.entries()).map(([g, items]) => ({ grupo: g, items }));
  }, [umbrales]);

  // Edición inline: una clave a la vez.
  const [editClave, setEditClave] = useState<string | null>(null);
  const [editUmbral, setEditUmbral] = useState('');
  const [editComparador, setEditComparador] = useState<ComparadorUmbral>('gte');
  const [editUnidad, setEditUnidad] = useState('');

  const iniciarEdicion = (u: UmbralAlerta) => {
    setEditClave(u.clave);
    setEditUmbral(String(u.umbral));
    setEditComparador(u.comparador);
    setEditUnidad(u.unidad ?? '');
  };
  const cancelar = () => setEditClave(null);
  const guardar = (u: UmbralAlerta) => {
    const n = Number(editUmbral);
    if (!Number.isFinite(n)) return;
    const unidadTrim = editUnidad.trim();
    actualizar.mutate(
      {
        clave: u.clave,
        umbral: n,
        comparador: editComparador,
        unidad: unidadTrim === '' ? null : unidadTrim,
      },
      {
        onSuccess: (r) => {
          if (r.data) setEditClave(null);
        },
      },
    );
  };
  const toggleActivo = (u: UmbralAlerta) => {
    actualizar.mutate({ clave: u.clave, activo: !u.activo });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Umbrales de alerta</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Valores configurables que disparan alertas en la tabla de batches y la evaluación de
          cumplimiento del laboratorio. Cambios afectan a todos los usuarios.
        </p>
      </div>

      {errorBackend && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="p-4 text-sm text-destructive">{errorBackend}</CardContent>
        </Card>
      )}

      {consulta.isLoading && (
        <Card>
          <CardContent className="space-y-2 p-4">
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-6 w-2/3" />
          </CardContent>
        </Card>
      )}

      {!consulta.isLoading &&
        grupos.map(({ grupo, items }) => (
          <Card key={grupo}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium capitalize">{grupo}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {items.map((u) => {
                const enEdicion = editClave === u.clave;
                return (
                  <div
                    key={u.clave}
                    className={cn(
                      'flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between',
                      !u.activo && 'opacity-60',
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-xs text-muted-foreground">{u.clave}</div>
                      <div className="text-sm">
                        {u.descripcion ?? <span className="italic text-muted-foreground">—</span>}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {enEdicion ? (
                        <>
                          <Select
                            value={editComparador}
                            onValueChange={(v) => setEditComparador(v as ComparadorUmbral)}
                          >
                            <SelectTrigger className="h-8 w-[140px] text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {COMPARADORES_UMBRAL.map((c) => (
                                <SelectItem key={c} value={c}>
                                  {ETIQUETA_COMPARADOR[c]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            step="any"
                            value={editUmbral}
                            onChange={(e) => setEditUmbral(e.target.value)}
                            className="h-8 w-[100px] text-right font-mono"
                          />
                          <Input
                            type="text"
                            value={editUnidad}
                            onChange={(e) => setEditUnidad(e.target.value)}
                            placeholder="%"
                            maxLength={20}
                            className="h-8 w-[60px] font-mono"
                            title="Unidad (ej. %, L). Dejá vacío para sin unidad."
                          />
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-8 w-8"
                            disabled={actualizar.isPending}
                            onClick={() => guardar(u)}
                            title="Guardar"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={cancelar}
                            title="Cancelar"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="rounded-md border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs">
                            {ETIQUETA_COMPARADOR[u.comparador]}
                          </span>
                          <span className="min-w-[80px] text-right font-mono text-sm font-semibold">
                            {u.umbral}
                            {u.unidad && (
                              <span className="ml-0.5 text-xs font-normal text-muted-foreground">
                                {u.unidad}
                              </span>
                            )}
                          </span>
                          <span
                            className={cn(
                              'inline-flex rounded-full border px-2 py-0.5 text-xs',
                              u.activo
                                ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                                : 'border-zinc-300 bg-zinc-100 text-zinc-500',
                            )}
                          >
                            {u.activo ? 'Activo' : 'Inactivo'}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 gap-1.5"
                            onClick={() => iniciarEdicion(u)}
                            disabled={actualizar.isPending}
                          >
                            <Pencil className="h-3.5 w-3.5" /> Editar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8"
                            onClick={() => toggleActivo(u)}
                            disabled={actualizar.isPending}
                          >
                            {u.activo ? 'Desactivar' : 'Activar'}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))}
    </div>
  );
}
